<template>
  <div class="download-page">
    <div class="terminal-container">

      <!-- Header -->
      <header class="terminal-header">
        <div class="terminal-prompt">
          <span class="prompt-user">xoul</span><span class="prompt-at">@</span><span class="prompt-host">local</span>
          <span class="prompt-colon">:</span><span class="prompt-path">~</span><span class="prompt-dollar">$</span>
          <span class="prompt-cmd">ls releases/</span>
          <span class="cursor">▊</span>
        </div>
      </header>

      <!-- Title -->
      <section class="section readme-hero">
        <h1 class="page-title">
          <span class="bracket">[</span> {{ $t('download.title') }} <span class="bracket">]</span>
        </h1>
        <p class="page-subtitle">{{ $t('download.subtitle') }}</p>
      </section>

      <!-- Install Guide -->
      <div class="terminal-divider">
        <span>── install ──</span>
      </div>

      <section class="section install-guide">
        <div class="install-option">
          <div class="install-option-header">
            <span class="install-number">1.</span>
            <span class="install-title">{{ $t('download.install_scratch_title') }}</span>
            <span class="install-time">~20 min</span>
          </div>
          <p class="install-desc">{{ $t('download.install_scratch_desc') }}</p>
        </div>
        <div class="install-option recommended-option">
          <div class="install-option-header">
            <span class="install-number">2.</span>
            <span class="install-title">{{ $t('download.install_image_title') }}</span>
            <span class="install-time fast">~5 min ⚡</span>
          </div>
          <p class="install-desc">{{ $t('download.install_image_desc') }}</p>
        </div>
      </section>

      <!-- Releases -->
      <div class="terminal-divider">
        <span>── releases ──</span>
      </div>

      <!-- Loading state -->
      <section class="section loading-state" v-if="loading">
        <div class="loading-box">
          <span class="loading-spinner">⟳</span>
          <p class="loading-text">{{ $t('download.loading') || 'Loading releases...' }}</p>
        </div>
      </section>

      <!-- Error state -->
      <section class="section error-state" v-else-if="error">
        <div class="error-box">
          <span class="error-icon">⚠</span>
          <p class="error-text">{{ error }}</p>
          <button class="retry-btn" @click="fetchReleases">↻ Retry</button>
        </div>
      </section>

      <!-- Releases -->
      <section class="section releases-section" v-else-if="releases.length">
        <div class="release-card" v-for="(rel, idx) in releases" :key="rel.id"
             :class="{ latest: idx === 0 }">
          <div class="release-header">
            <span class="release-version">{{ rel.version }}</span>
            <span class="release-badge" v-if="rel.prerelease">pre-release</span>
            <span class="release-badge latest-badge" v-if="idx === 0">{{ $t('download.latest') }}</span>
            <span class="release-date">{{ rel.date }}</span>
          </div>
          <div class="release-notes" v-if="rel.notes" v-html="renderNotes(rel.notes)"></div>
          <div class="release-assets" v-if="rel.assets.length">
            <a v-for="asset in rel.assets" :key="asset.name"
               :href="asset.url" class="release-download" target="_blank" rel="noopener">
              → {{ asset.name }} <span class="asset-size">({{ asset.size }})</span>
            </a>
            <a href="https://www.xoulai.net/xoul_dist/xoul.qcow2"
               class="release-download" download="xoul.qcow2">
              → xoul.qcow2 <span class="asset-size">(Agent VM Image)</span>
            </a>
            <p class="vm-hint">⚡ {{ $t('download.vm_hint') }}</p>
          </div>
          <a v-else :href="rel.html_url" class="release-download" target="_blank" rel="noopener">
            → {{ $t('download.download_link') }}
          </a>
        </div>
      </section>

      <!-- Empty state -->
      <section class="section empty-state" v-else>
        <div class="empty-box">
          <span class="empty-icon">📦</span>
          <p class="empty-text">{{ $t('download.coming_soon') }}</p>
        </div>
      </section>

      <!-- Footer -->
      <footer class="terminal-footer">
        <div class="terminal-prompt">
          <span class="prompt-user">xoul</span><span class="prompt-at">@</span><span class="prompt-host">local</span>
          <span class="prompt-colon">:</span><span class="prompt-path">~</span><span class="prompt-dollar">$</span>
          <span class="cursor blink">▊</span>
        </div>
      </footer>

    </div>
  </div>
</template>

<script>
const GITHUB_REPO = 'xoul-project/xoul'
const GITHUB_API = `https://api.github.com/repos/${GITHUB_REPO}/releases`

export default {
  name: 'DownloadView',
  data() {
    return {
      releases: [],
      loading: true,
      error: null,
    }
  },
  mounted() {
    this.fetchReleases()
  },
  methods: {
    async fetchReleases() {
      this.loading = true
      this.error = null
      try {
        const res = await fetch(GITHUB_API, {
          headers: { Accept: 'application/vnd.github.v3+json' },
        })
        if (!res.ok) throw new Error(`GitHub API error: ${res.status}`)
        const data = await res.json()
        this.releases = data.map(r => ({
          id: r.id,
          version: r.tag_name,
          date: new Date(r.published_at).toLocaleDateString(),
          notes: r.body || '',
          prerelease: r.prerelease,
          html_url: r.html_url,
          assets: (r.assets || []).map(a => ({
            name: a.name,
            url: a.browser_download_url,
            size: this.formatSize(a.size),
          })),
        }))
      } catch (e) {
        console.error('Failed to fetch releases:', e)
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
      if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB'
      return (bytes / 1073741824).toFixed(2) + ' GB'
    },
    renderNotes(md) {
      // Basic markdown → HTML: headers, bold, code, links, line breaks
      return md
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/^### (.+)$/gm, '<h4>$1</h4>')
        .replace(/^## (.+)$/gm, '<h3>$1</h3>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
        .replace(/^[-*] (.+)$/gm, '• $1')
        .replace(/\n/g, '<br>')
    },
  },
}
</script>

<style scoped>
/* ── Page ── */
.download-page {
  position: relative;
  min-height: 100vh;
  background: transparent;
  color: #b0b0b0;
  font-family: 'Fira Code', 'Cascadia Code', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
}

.terminal-container {
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
  padding: 24px 32px 40px;
}

/* ── Terminal Prompt ── */
.terminal-header {
  margin-bottom: 32px;
  padding-top: 12px;
}

.terminal-prompt {
  font-size: 15px;
  letter-spacing: 0.5px;
}

.prompt-user { color: #50c878; }
.prompt-at { color: #555; }
.prompt-host { color: #50c878; }
.prompt-colon { color: #555; }
.prompt-path { color: #6a9fb5; }
.prompt-dollar { color: #555; margin-right: 8px; }
.prompt-cmd { color: #e0e0e0; }

.cursor {
  color: #50c878;
  opacity: 0.8;
}

.cursor.blink {
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* ── Hero ── */
.section {
  margin-bottom: 24px;
}

.readme-hero {
  margin-bottom: 24px;
  background: rgba(8, 8, 8, 0.80);
  padding: 24px;
  border-radius: 8px;
}

.page-title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 900;
  color: #50c878;
  letter-spacing: 2px;
  margin-bottom: 12px;
  text-shadow: 0 0 30px rgba(80, 200, 120, 0.3);
}

.bracket {
  color: #3a6a4a;
  font-weight: 400;
}

.page-subtitle {
  color: #888;
  font-size: 14px;
}

/* ── Divider ── */
.terminal-divider {
  color: #333;
  font-size: 12px;
  margin: 24px 0 20px;
  letter-spacing: 1px;
}

.terminal-divider span {
  color: #444;
}

/* ── Release Cards ── */
.releases-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.release-card {
  padding: 20px 24px;
  border: 1px solid #1a1a1a;
  border-radius: 8px;
  background: rgba(8, 8, 8, 0.80);
  transition: border-color 0.3s, box-shadow 0.3s;
}

.release-card:hover {
  border-color: rgba(80, 200, 120, 0.2);
  box-shadow: 0 0 30px rgba(80, 200, 120, 0.03);
}

.release-card.latest {
  border-color: rgba(80, 200, 120, 0.3);
}

.release-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.release-version {
  font-size: 18px;
  font-weight: 700;
  color: #e0e0e0;
  letter-spacing: 0.5px;
}

.release-badge {
  font-size: 10px;
  font-weight: 600;
  color: #50c878;
  background: rgba(80, 200, 120, 0.1);
  border: 1px solid rgba(80, 200, 120, 0.25);
  padding: 2px 8px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.release-date {
  color: #555;
  font-size: 12px;
  margin-left: auto;
}

.release-notes {
  color: #777;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 14px;
}

.release-download {
  display: inline-block;
  color: #50c878;
  font-size: 13px;
  text-decoration: none;
  opacity: 0.7;
  transition: opacity 0.2s;
  letter-spacing: 0.3px;
}

.release-download:hover {
  opacity: 1;
  text-decoration: underline;
}

/* ── Empty State ── */
.empty-state {
  display: flex;
  justify-content: center;
}

.empty-box {
  text-align: center;
  padding: 48px 32px;
  background: rgba(8, 8, 8, 0.80);
  border: 1px dashed #222;
  border-radius: 8px;
  width: 100%;
}

.empty-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-text {
  color: #555;
  font-size: 14px;
  letter-spacing: 0.5px;
}

/* ── Loading State ── */
.loading-state {
  display: flex;
  justify-content: center;
}

.loading-box {
  text-align: center;
  padding: 48px 32px;
  background: rgba(8, 8, 8, 0.80);
  border: 1px solid #1a1a1a;
  border-radius: 8px;
  width: 100%;
}

.loading-spinner {
  font-size: 36px;
  display: inline-block;
  color: #50c878;
  animation: spin 1.5s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-text {
  color: #555;
  font-size: 14px;
  margin-top: 12px;
}

/* ── Error State ── */
.error-state {
  display: flex;
  justify-content: center;
}

.error-box {
  text-align: center;
  padding: 32px;
  background: rgba(8, 8, 8, 0.80);
  border: 1px solid rgba(255, 80, 80, 0.2);
  border-radius: 8px;
  width: 100%;
}

.error-icon {
  font-size: 36px;
  color: #ff5050;
  display: block;
  margin-bottom: 12px;
}

.error-text {
  color: #888;
  font-size: 13px;
  margin-bottom: 16px;
}

.retry-btn {
  background: rgba(80, 200, 120, 0.1);
  border: 1px solid rgba(80, 200, 120, 0.3);
  color: #50c878;
  padding: 6px 16px;
  border-radius: 4px;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.retry-btn:hover {
  background: rgba(80, 200, 120, 0.2);
}

/* ── Release Assets ── */
.release-assets {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.asset-size {
  color: #555;
  font-size: 11px;
}

/* ── Release Notes Rendered ── */
.release-notes h3,
.release-notes h4 {
  color: #ccc;
  margin: 8px 0 4px;
  font-size: 14px;
}

.release-notes code {
  background: rgba(80, 200, 120, 0.08);
  color: #50c878;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

.release-notes a {
  color: #6a9fb5;
  text-decoration: none;
}

.release-notes a:hover {
  text-decoration: underline;
}

.release-notes strong {
  color: #ccc;
}

.latest-badge {
  background: rgba(80, 200, 120, 0.15) !important;
}

/* ── Install Guide ── */
.install-guide {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.install-option {
  padding: 14px 18px;
  border: 1px solid #1a1a1a;
  border-radius: 8px;
  background: rgba(8, 8, 8, 0.80);
}

.recommended-option {
  border-color: rgba(255, 180, 50, 0.3);
  background: rgba(255, 180, 50, 0.03);
}

.install-option-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.install-number {
  color: #50c878;
  font-weight: 700;
  font-size: 14px;
}

.install-title {
  color: #e0e0e0;
  font-weight: 600;
  font-size: 13px;
}

.install-time {
  margin-left: auto;
  color: #555;
  font-size: 11px;
  padding: 2px 8px;
  border: 1px solid #222;
  border-radius: 4px;
}

.install-time.fast {
  color: #ffb432;
  border-color: rgba(255, 180, 50, 0.3);
  background: rgba(255, 180, 50, 0.08);
}

.install-desc {
  color: #777;
  font-size: 12px;
  padding-left: 18px;
}

/* ── VM Hint ── */
.vm-hint {
  color: #ffb432;
  font-size: 11px;
  margin: 4px 0 0 16px;
  opacity: 0.85;
  letter-spacing: 0.2px;
}

/* ── Footer ── */
.terminal-footer {
  margin-top: 32px;
  padding-top: 16px;
  border-top: 1px solid #1a1a1a;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .terminal-container {
    padding: 16px 16px 24px;
  }

  .page-title {
    font-size: 1.8rem;
  }
}
</style>
